import React, { useMemo } from 'react';
import { MONTHS, computeMonthlyNeed } from '../calculations/monthlyNeedCalculations.js';
import { computeCollectedMonthly } from '../calculations/roofCollectionCalculations.js';
import { ComparisonBarChart, CumulativeLineChart, ChartLegend } from '../ComparisonCharts.js';
import ChartExportToolbar from '../ChartExportToolbar.js';

/**
 * Étape 4 : comparaison directe besoin (étape 1) vs collecté (étape 3), sans tenir compte
 * d'un éventuel stockage tampon — c'est l'objet de l'étape 5.
 */
const BalanceStep = ({ usages, roofs, rainfall, currentStep, totalSteps, goPrevious, goNext, canAdvance }) => {
  const need = useMemo(() => computeMonthlyNeed(usages), [usages]);
  const collected = useMemo(() => computeCollectedMonthly(roofs, rainfall), [roofs, rainfall]);

  const needTotal = need.reduce((a, b) => a + b, 0);
  const collectedTotal = collected.reduce((a, b) => a + b, 0);

  const coverageMax = needTotal > 0 ? Math.min(collectedTotal, needTotal) / needTotal * 100 : 0;
  const coverageRaw = needTotal > 0 ? need.reduce((a, v, i) => a + Math.min(v, collected[i]), 0) / needTotal * 100 : 0;

  let comment;
  if (needTotal === 0) {
    comment = "Aucun besoin renseigné à l'étape 1 : activez au moins un usage pour voir le bilan.";
  } else if (collectedTotal >= needTotal) {
    comment = `L'eau collectée sur l'année dépasse le besoin annuel (surplus de ${(collectedTotal - needTotal).toFixed(1)} m³/an). Avec une cuve correctement dimensionnée pour lisser les écarts entre mois, la quasi-totalité du besoin est théoriquement couvrable — c'est ce que l'étape 5 va vérifier précisément.`;
  } else {
    comment = `Même en stockant parfaitement toute l'eau collectée, il manquerait ${(needTotal - collectedTotal).toFixed(1)} m³/an (couverture max. théorique : ${coverageMax.toFixed(0)}%). Pour aller plus loin, il faudra soit réduire les usages couverts, soit augmenter la surface de collecte.`;
  }

  const getComparisonTSV = () => {
    const rows = ['Mois\tBesoin (m³)\tCollecté (m³)'];
    MONTHS.forEach((m, i) => rows.push(`${m}\t${need[i].toFixed(3)}\t${collected[i].toFixed(3)}`));
    return rows.join('\n');
  };

  const getCumulativeTSV = () => {
    let cn = 0, cc = 0;
    const rows = ['Mois\tBesoin cumulé (m³)\tCollecté cumulé (m³)'];
    MONTHS.forEach((m, i) => { cn += need[i]; cc += collected[i]; rows.push(`${m}\t${cn.toFixed(3)}\t${cc.toFixed(3)}`); });
    return rows.join('\n');
  };

  return (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">Récupération d'eau de pluie</span>
          <h2 className="apv2-step-heading">Bilan besoin / ressource</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {totalSteps}</span>
      </div>
      <div className="apv2-step-body">

        <ChartLegend needLabel="Besoin" collectLabel="Collecté" />
        <div className="rw-chart-block">
          <p className="rw-chart-title">Besoin vs eau collectée, mois par mois</p>
          <ChartExportToolbar containerId="rw-compare-chart" getDataTSV={getComparisonTSV} />
          <div id="rw-compare-chart">
            <ComparisonBarChart need={need} collected={collected} />
          </div>
        </div>

        <div className="apv2-flat-table-wrapper">
          <table className="apv2-flat-table">
            <thead>
              <tr><th>Mois</th><th>Besoin (m³)</th><th>Collecté (m³)</th><th>Solde (m³)</th></tr>
            </thead>
            <tbody>
              {MONTHS.map((m, i) => {
                const solde = collected[i] - need[i];
                return (
                  <tr key={m}>
                    <td>{m}</td>
                    <td>{need[i].toFixed(2)}</td>
                    <td>{collected[i].toFixed(2)}</td>
                    <td className={solde >= 0 ? 'rw-solde-pos' : 'rw-solde-neg'}>{solde >= 0 ? '+' : ''}{solde.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="apv2-summary-cards">
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{needTotal.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m³</span>
            <span className="apv2-summary-label">besoin annuel</span>
          </div>
          <div className="apv2-summary-card">
            <span className="apv2-summary-value">{collectedTotal.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m³</span>
            <span className="apv2-summary-label">collecté annuel</span>
          </div>
          <div className="apv2-summary-card apv2-summary-card--secondary">
            <span className="apv2-summary-value">{coverageMax.toFixed(0)}%</span>
            <span className="apv2-summary-label">couverture max. théorique</span>
          </div>
          <div className="apv2-summary-card apv2-summary-card--secondary">
            <span className="apv2-summary-value">{coverageRaw.toFixed(0)}%</span>
            <span className="apv2-summary-label">couverture sans stockage</span>
          </div>
        </div>

        <p className="apv2-sim-hint">{comment}</p>

        <ChartLegend needLabel="Besoin cumulé" collectLabel="Collecté cumulé" />
        <div className="rw-chart-block">
          <p className="rw-chart-title">Courbe cumulée sur l'année</p>
          <ChartExportToolbar containerId="rw-cumulative-chart" getDataTSV={getCumulativeTSV} />
          <div id="rw-cumulative-chart">
            <CumulativeLineChart need={need} collected={collected} />
          </div>
          <p className="apv2-sim-hint">
            Quand la courbe « collecté » passe sous la courbe « besoin », il faut puiser dans une réserve
            constituée les mois précédents — c'est cet écart qui guide le dimensionnement de la cuve à l'étape 5.
          </p>
        </div>
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <button type="button" className="apv2-footer-btn-cta" onClick={goNext} disabled={!canAdvance}>
          Suivant →
        </button>
      </div>
    </div>
  );
};

export default BalanceStep;
